import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiEye, FiAlertTriangle, FiChevronLeft } from "react-icons/fi";
import { ApiContext } from "../../../context/MyContext";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { EmptyState } from "../../../common/comms/EmptyState";
import { Toaster } from "../../../common/Toaster/Toaster";
import {
  get as commsGet,
  post as commsPost,
} from "../../../service/comms/commsApi";
import { getRoom } from "../../../service/comms/roomsService";
import {
  aiMute,
  getCrisisFlags,
} from "../../../service/comms/oversightService";
import { Thread } from "../Messages/Thread";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";

// Module-scoped: once a manager hits a 403 on ai-mute, hide the toggle for
// the rest of the session (across room navigations); resets on full reload.
// Keyed to the empId it was set for — the SPA doesn't reload on
// login/logout, so without this a 403 recorded for one manager would keep
// hiding the toggle for whoever logs in next.
let aiMuteForbiddenSession = false;
let aiMuteForbiddenSessionEmpId = null;

/**
 * Shadow (oversight) room view (`/oversight/:roomId`).
 *
 * Read-only observation of a client's coaching room. Provides a company-wide AI
 * mute toggle and surfaces unresolved crisis flags. Reuses the Messages Thread
 * in read-only SHADOW mode so the composer is disabled.
 *
 * Resilient to an unreachable ms-agents backend: renders the shell (banners,
 * toggle, empty thread) instead of crashing. A 403 on `getRoom` means the
 * viewer's dynamic SHADOW grant doesn't cover this room (outside their report
 * chain) — that renders a full-panel EmptyState instead of the Thread, since
 * the Thread's own message fetch would also 403.
 */
export default function OversightRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { empId } = useContext(ApiContext);
  const { getClientName } = useCommsIdentity();

  const [room, setRoom] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [aiMuted, setAiMuted] = useState(false);
  const [muting, setMuting] = useState(false);
  const [aiMuteHidden, setAiMuteHidden] = useState(
    aiMuteForbiddenSession && aiMuteForbiddenSessionEmpId === empId
  );
  const [hasCrisis, setHasCrisis] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [toast, setToast] = useState(null);

  // Re-derive whenever the authenticated identity changes (login/logout
  // don't reload this SPA): a 403 recorded for a previous manager must not
  // keep hiding the toggle for whoever is logged in now.
  useEffect(() => {
    setAiMuteHidden(aiMuteForbiddenSession && aiMuteForbiddenSessionEmpId === empId);
  }, [empId]);

  // Hydrate room (for client label + company id) and crisis flags on mount.
  useEffect(() => {
    let active = true;
    if (!roomId) return undefined;

    getRoom(commsGet, roomId)
      .then((r) => {
        if (!active || !r) return;
        setRoom(r);
        if (r.company_id != null) setCompanyId(r.company_id);
        setAiMuted(r.ai_mode === "DISABLED");
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        }
        // Other errors: stay lenient — page shell still renders.
      });

    getCrisisFlags(commsGet, true)
      .then((payload) => {
        if (!active) return;
        const flags = payload?.flags ?? payload ?? [];
        setHasCrisis(Array.isArray(flags) && flags.length > 0);
      })
      .catch(() => active && setHasCrisis(false));

    return () => {
      active = false;
    };
  }, [roomId]);

  const clientLabel = room
    ? room.client_name || getClientName(room.client_id) || `Client ${room.client_id ?? ""}`.trim()
    : "this client";

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/coaches?tab=rooms");
    }
  };

  const handleToggleMute = async () => {
    const nextMuted = !aiMuted;
    setMuting(true);
    setAiMuted(nextMuted); // optimistic
    try {
      await aiMute(commsPost, {
        company_id: companyId,
        ai_mode: nextMuted ? "DISABLED" : "ENABLED",
      });
      setToast({
        message: nextMuted
          ? "AI muted company-wide"
          : "AI resumed company-wide",
        type: "success",
      });
    } catch (err) {
      setAiMuted(!nextMuted); // roll back
      if (err?.response?.status === 403) {
        aiMuteForbiddenSession = true;
        aiMuteForbiddenSessionEmpId = empId;
        setAiMuteHidden(true);
        setToast({
          message: "Only executives can change AI mode",
          type: "error",
        });
      } else {
        setToast({ message: "Could not update AI mute", type: "error" });
      }
    } finally {
      setMuting(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4.5rem)] bg-gray-50">
      {/* Oversight header */}
      <header className="flex flex-wrap items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back to rooms"
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 flex-shrink-0"
        >
          <FiChevronLeft className="w-5 h-5" aria-hidden="true" />
        </button>

        <StatusBadge tone="internal" dot>
          <FiEye className="mr-1" size={12} />
          Shadow role · read-only
        </StatusBadge>

        {!aiMuteHidden && (
          <div className="w-full sm:w-auto sm:ml-auto flex items-center justify-between sm:justify-start gap-3">
            <span className="text-xs font-medium text-gray-600">
              Global AI mute
            </span>
            <button
              type="button"
              onClick={handleToggleMute}
              disabled={muting || companyId == null}
              aria-pressed={aiMuted}
              title={companyId == null ? "Room still loading" : undefined}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                aiMuted ? "bg-danger" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  aiMuted ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        )}
      </header>

      {forbidden ? (
        <div className="flex-1 min-h-0 flex items-center justify-center bg-white">
          <EmptyState
            title="You're not a participant of this room"
            hint="This room is outside your report chain."
            action={
              <button
                type="button"
                onClick={() => navigate("/coaches?tab=rooms")}
                className="min-h-[44px] px-4 text-sm font-medium text-white rounded-lg bg-orange-primary hover:opacity-90"
              >
                Back to rooms
              </button>
            }
          />
        </div>
      ) : (
        <>
          {/* Crisis banner */}
          {hasCrisis && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-danger-bg text-danger-dark text-sm font-medium border-b border-danger/30">
              <FiAlertTriangle size={16} />
              Unresolved crisis flag detected. Review immediately.
            </div>
          )}

          {/* Observation banner */}
          <div className="px-4 py-2.5 bg-gray-100 text-gray-600 text-xs border-b border-gray-200">
            Observing {clientLabel}'s room — participants are not notified. You
            cannot send messages.
          </div>

          {/* Read-only thread */}
          <div className="flex-1 min-h-0 bg-white">
            {roomId ? (
              <Thread
                roomId={roomId}
                viewerUserId={empId}
                viewerRole="SHADOW"
                readOnly
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-gray-400">
                No room selected.
              </div>
            )}
          </div>
        </>
      )}

      {toast && <Toaster message={toast.message} type={toast.type} />}
    </div>
  );
}
